import { useLayoutEffect, useRef, type HTMLAttributes, type ReactNode, type RefObject } from 'react';

/*
 * Появление блока при прокрутке (DESIGN.md, «Движение»): 1100 мс, сдвиг 40 px
 * вверх и прозрачность, один раз при видимости 15 %. Блок прячется только
 * после того, как подтверждено, что IntersectionObserver есть и
 * prefers-reduced-motion не включён; иначе (и в jsdom, и при любом сбое)
 * он виден сразу. Классы и сами переходы лежат в index.css.
 */
const THRESHOLD = 0.15;
const CASCADE_STEP_MS = 120;

interface RevealProps extends HTMLAttributes<HTMLElement> {
  as?: 'div' | 'li' | 'section';
  /** Номер среди соседей: i-я карточка ряда появляется на i × 120 мс позже. */
  order?: number;
  children: ReactNode;
}

export function Reveal({ as = 'div', order = 0, children, ...rest }: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  // До первой отрисовки, чтобы блок не мигнул в конечном виде перед скрытием.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const finish = () => {
      element.classList.remove('reveal-armed', 'reveal-in');
      element.style.removeProperty('--reveal-delay');
    };
    let observer: IntersectionObserver | undefined;
    try {
      element.style.setProperty('--reveal-delay', `${order * CASCADE_STEP_MS}ms`);
      element.classList.add('reveal-armed');
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          element.classList.add('reveal-in');
          observer?.disconnect();
        },
        { threshold: THRESHOLD, rootMargin: '0px 0px -6% 0px' },
      );
      observer.observe(element);
    } catch {
      // Украшение не должно прятать содержимое: при сбое блок остаётся виден.
      finish();
    }

    const onEnd = (event: TransitionEvent) => {
      if (event.target === element && event.propertyName === 'transform') finish();
    };
    element.addEventListener('transitionend', onEnd);
    return () => {
      observer?.disconnect();
      element.removeEventListener('transitionend', onEnd);
      finish();
    };
  }, [order]);

  if (as === 'li') {
    return (
      <li ref={ref as RefObject<HTMLLIElement>} {...rest}>
        {children}
      </li>
    );
  }
  if (as === 'section') {
    return (
      <section ref={ref} {...rest}>
        {children}
      </section>
    );
  }
  return (
    <div ref={ref as RefObject<HTMLDivElement>} {...rest}>
      {children}
    </div>
  );
}
